import { ExternalLink, Loader2 } from "lucide-react";

import { Button } from "@patient-watch/ui/button";
import { cn } from "@patient-watch/ui/utils";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { isImageFileName, isPdfFileName } from "../utils/fileUploadHelpers";

interface FileUploadPreviewProps {
    fileName: string;
    previewUrl?: string | null;
    isLoading?: boolean;
    isError?: boolean;
    size?: "sm" | "md" | "lg";
    className?: string;
    showActions?: boolean;
}

function previewMaxHeight(size: FileUploadPreviewProps["size"]) {
    if (size === "sm") return "max-h-40";
    if (size === "lg") return "max-h-72";
    return "max-h-56";
}

function pdfPreviewHeight(size: FileUploadPreviewProps["size"]) {
    if (size === "sm") return "h-48";
    if (size === "lg") return "h-80";
    return "h-64";
}

export function FileUploadPreview({
    fileName,
    previewUrl,
    isLoading = false,
    isError = false,
    size = "md",
    className,
    showActions = true,
}: FileUploadPreviewProps) {
    const { locale } = useAppLocale();
    const copy =
        locale === "fr-FR"
            ? {
                  uploadedFile: "Fichier envoyé",
                  loadingPreview: "Chargement de l'aperçu...",
                  couldNotLoadPreview:
                      "Impossible de charger l'aperçu. Vous pouvez tout de même ouvrir le fichier ci-dessous.",
                  openFile: "Ouvrir le fichier",
                  download: "Télécharger",
              }
            : locale === "it-IT"
              ? {
                    uploadedFile: "File caricato",
                    loadingPreview: "Caricamento dell'anteprima...",
                    couldNotLoadPreview: "Impossibile caricare l'anteprima. Può comunque aprire il file qui sotto.",
                    openFile: "Apri il file",
                    download: "Scarica",
                }
              : locale === "es-ES"
                ? {
                      uploadedFile: "Archivo cargado",
                      loadingPreview: "Cargando la vista previa...",
                      couldNotLoadPreview:
                          "No se ha podido cargar la vista previa. Aun así puede abrir el archivo a continuación.",
                      openFile: "Abrir el archivo",
                      download: "Descargar",
                  }
                : locale === "de-DE"
                  ? {
                        uploadedFile: "Hochgeladene Datei",
                        loadingPreview: "Vorschau wird geladen...",
                        couldNotLoadPreview:
                            "Vorschau konnte nicht geladen werden. Sie können die Datei unten trotzdem öffnen.",
                        openFile: "Datei öffnen",
                        download: "Herunterladen",
                    }
                  : locale === "da-DK"
                    ? {
                          uploadedFile: "Uploadet fil",
                          loadingPreview: "Indlæser forhåndsvisning...",
                          couldNotLoadPreview:
                              "Forhåndsvisningen kunne ikke indlæses. Du kan stadig åbne filen nedenfor.",
                          openFile: "Åbn fil",
                          download: "Download",
                      }
                    : {
                          uploadedFile: "Uploaded file",
                          loadingPreview: "Loading preview...",
                          couldNotLoadPreview: "Could not load preview. You can still open the file below.",
                          openFile: "Open file",
                          download: "Download",
                      };

    const displayName = fileName.trim() || copy.uploadedFile;
    const showImagePreview = Boolean(previewUrl && isImageFileName(fileName));
    const showPdfPreview = Boolean(previewUrl && isPdfFileName(fileName));
    const maxHeightClass = previewMaxHeight(size);

    return (
        <div className={cn("flex flex-col gap-3 min-w-0", className)}>
            {isLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground text-lg">
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    <span>{copy.loadingPreview}</span>
                </div>
            ) : null}

            {isError ? <p className="text-red-700 text-lg">{copy.couldNotLoadPreview}</p> : null}

            {showImagePreview ? (
                <div
                    className={cn(
                        "print-file-upload-image flex justify-center items-center bg-muted/20 p-2 border border-border rounded-md w-full overflow-hidden",
                        maxHeightClass
                    )}
                >
                    <img
                        src={previewUrl ?? undefined}
                        alt={displayName}
                        className={cn("block w-auto h-auto max-w-full object-contain", maxHeightClass)}
                    />
                </div>
            ) : null}

            {showPdfPreview ? (
                <iframe
                    title={`Preview of ${displayName}`}
                    src={previewUrl ?? undefined}
                    className={cn("bg-white border border-border rounded-md w-full", pdfPreviewHeight(size))}
                />
            ) : null}

            {showActions && previewUrl ? (
                <div className="print:hidden flex flex-wrap items-center gap-2">
                    <Button type="button" variant="secondary" size="sm" asChild>
                        <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" aria-hidden="true" />
                            {copy.openFile}
                        </a>
                    </Button>
                    <Button type="button" variant="outline" size="sm" asChild>
                        <a href={previewUrl} download={displayName}>
                            {copy.download}
                        </a>
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
